//
// Copyright 2024 DXOS.org
//

import { completionStatus } from '@codemirror/autocomplete';
import { type Extension } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import React, { type KeyboardEvent } from 'react';

import { useThemeContext } from '@dxos/react-ui';
import { type UseTextEditorProps, useTextEditor } from '@dxos/react-ui-editor';
import {
  type ThemeExtensionsOptions,
  createBasicExtensions,
  createThemeExtensions,
  filterChars,
} from '@dxos/ui-editor';
import { mx } from '@dxos/ui-theme';

import { type GridEditBox } from '../Grid';

export type EditorKeyEvent = Pick<KeyboardEvent<HTMLInputElement>, 'key'> & { shift?: boolean };

export type EditorKeyHandler = (value: string | undefined, event: EditorKeyEvent) => void;
export type EditorBlurHandler = (value: string | undefined) => void;
export type EditorKeyOrBlurHandler = (value: string | undefined, event?: EditorKeyEvent) => void;

export type EditorKeysProps = {
  onClose: EditorKeyHandler;
  onNav?: EditorKeyHandler;
};

// TODO(Zan): Should each consumer be responsible for defining these?
export const editorKeys = ({ onNav, onClose }: EditorKeysProps): Extension => {
  return keymap.of([
    {
      key: 'ArrowUp',
      run: (editor) => {
        const value = editor.state.doc.toString();
        onNav?.(value, { key: 'ArrowUp' });
        return !!onNav;
      },
    },
    {
      key: 'ArrowDown',
      run: (editor) => {
        const value = editor.state.doc.toString();
        onNav?.(value, { key: 'ArrowDown' });
        return !!onNav;
      },
    },
    {
      key: 'Mod-ArrowLeft',
      run: (editor) => {
        const value = editor.state.doc.toString();
        onNav?.(value, { key: 'ArrowLeft' });
        return !!onNav;
      },
    },
    {
      key: 'Mod-ArrowRight',
      run: (editor) => {
        const value = editor.state.doc.toString();
        onNav?.(value, { key: 'ArrowRight' });
        return !!onNav;
      },
    },
    {
      key: 'Enter',
      run: (editor) => {
        if (completionStatus(editor.state)) {
          return false;
        } else {
          onClose(editor.state.doc.toString(), { key: 'Enter' });
          return true;
        }
      },
      shift: (editor) => {
        if (completionStatus(editor.state)) {
          return false;
        } else {
          onClose(editor.state.doc.toString(), { key: 'Enter', shift: true });
          return true;
        }
      },
    },
    {
      key: 'Tab',
      run: (editor) => {
        if (completionStatus(editor.state)) {
          return false;
        } else {
          onClose(editor.state.doc.toString(), { key: 'Tab' });
          return true;
        }
      },
      shift: (editor) => {
        if (completionStatus(editor.state)) {
          return false;
        } else {
          onClose(editor.state.doc.toString(), { key: 'Tab', shift: true });
          return true;
        }
      },
    },
    {
      key: 'Escape',
      run: () => {
        onClose(undefined, { key: 'Escape' });
        return true;
      },
    },
  ]);
};

export type CellEditorProps = {
  value?: string;
  extensions?: Extension;
  box?: GridEditBox;
  gridId?: string;
  onBlur?: EditorBlurHandler;
} & Pick<UseTextEditorProps, 'autoFocus'> &
  Pick<ThemeExtensionsOptions, 'slots'>;

export const CellEditor = ({ value, extensions, box, gridId, autoFocus, slots, onBlur }: CellEditorProps) => {
  const { themeMode } = useThemeContext();
  const { parentRef } = useTextEditor(() => {
    return {
      autoFocus,
      initialValue: value,
      selection: { anchor: value?.length ?? 0 },
      extensions: [
        extensions ?? [],
        filterChars(/[\n\r]+/),
        // Observe the underlying blur DOM event rather than `EditorView.focusChangeEffect`. The
        // focus-change facet fires asynchronously (CodeMirror schedules it on a 10ms setTimeout),
        // which means in React strict mode the destroy → blur of the first EditorView runs the
        // callback after the second view has mounted — committing stale data and closing the
        // editor on the user's first keystroke. Deferring via `queueMicrotask` runs the check
        // *after* `view.destroy()` finishes its synchronous body (which calls `dom.remove()`),
        // so `view.dom.isConnected === false` reliably distinguishes a programmatic teardown
        // from a real user blur. Pass `undefined` on teardown so downstream handlers can consume
        // any pending suppress-next-blur flag without committing stale data.
        EditorView.domEventObservers({
          blur: (_event, view) => {
            const doc = view.state.doc.toString();
            queueMicrotask(() => {
              onBlur?.(view.dom.isConnected ? doc : undefined);
            });
          },
        }),
        createBasicExtensions({ lineWrapping: true }),
        createThemeExtensions({
          themeMode,
          slots: {
            editor: {
              className: mx(
                'min-w-full! w-min! !max-w-(--dx-grid-cell-editor-max-inline-size) min-h-full! !max-h-(--dx-grid-cell-editor-max-block-size)',
                slots?.editor?.className,
              ),
            },
            scroller: {
              className: mx(
                'overflow-x-hidden! !py-[max(0,calc(var(--dx-grid-cell-editor-padding-block)-1px))] pe-0! !pl-(--dx-grid-cell-editor-padding-inline)',
                slots?.scroller?.className,
              ),
            },
            content: {
              className: mx('break-normal!', slots?.content?.className),
            },
          },
        }),
      ],
    };
  }, [extensions, autoFocus, value, onBlur, themeMode, slots]);

  return (
    <div
      data-testid='grid.cell-editor'
      ref={parentRef}
      className='absolute z-[1] dx-grid__cell-editor'
      style={{
        insetInlineStart: box?.insetInlineStart ?? '0px',
        insetBlockStart: box?.insetBlockStart ?? '0px',
        minInlineSize: box?.inlineSize ?? '180px',
        minBlockSize: box?.blockSize ?? '30px',
        ...{ '--dx-grid-cell-width': `${box?.inlineSize ?? 200}px` },
      }}
      {...(gridId && { 'data-grid': gridId })}
    />
  );
};
